import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { type RegionId, regionIdSchema } from '@snootsvet/shared'

import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'

/** The region shown before the authenticated staff's regions have loaded. */
export const DEFAULT_REGION: RegionId = 'uk'

/** localStorage key for the last-used region (per-device view state). */
const ACTIVE_REGION_KEY = 'active-region'

interface RegionContextValue {
  isLoading: boolean
  region: RegionId
  setRegion: (region: RegionId) => void
  /** Regions the current staff member is assigned to (at least one). */
  regions: RegionId[]
}

const RegionContext = createContext<RegionContextValue | null>(null)

/** Reads the persisted active region from localStorage, ignoring anything invalid. */
function readPersistedRegion(): RegionId | null {
  const parsed = regionIdSchema.safeParse(localStorage.getItem(ACTIVE_REGION_KEY))
  return parsed.success ? parsed.data : null
}

/**
 * Picks the active region for a staff member: the persisted value if it is still
 * one of their assigned regions, otherwise the first assigned region.
 * Assumes `assigned` is non-empty (guaranteed by the min(1) schema).
 */
function resolveActiveRegion(assigned: RegionId[], persisted: RegionId | null): RegionId {
  if (persisted && assigned.includes(persisted)) return persisted
  return assigned[0]
}

/**
 * Holds the active region, scoped to the authenticated staff member's assigned
 * regions. The active region is restored from localStorage on load (per-device)
 * when it is still assigned, otherwise the first assigned region.
 */
export function RegionProvider({ children }: { children: ReactNode }) {
  const { data: staff } = useGetAuthenticatedStaff()
  const assigned = staff?.regions
  const [selectedRegion, setSelectedRegion] = useState<RegionId | null>(null)
  const region = selectedRegion ?? DEFAULT_REGION

  // Reconcile the active region once the assigned regions load.
  useEffect(() => {
    if (!assigned || assigned.length === 0) return
    const persistedRegion = readPersistedRegion()
    const resolvedRegion = resolveActiveRegion(assigned, persistedRegion)
    setSelectedRegion(resolvedRegion)
  }, [assigned])

  const setRegion = useCallback(
    (next: RegionId) => {
      // Ignore a region the staff member isn't assigned to.
      if (assigned && !assigned.includes(next)) return
      setSelectedRegion(next)
      localStorage.setItem(ACTIVE_REGION_KEY, next)
    },
    [assigned]
  )

  const value = useMemo<RegionContextValue>(
    () => ({
      isLoading: selectedRegion === null,
      region,
      setRegion,
      regions: assigned ?? [DEFAULT_REGION],
    }),
    [selectedRegion, region, setRegion, assigned]
  )
  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>
}

/**
 * Returns the *viewer's* active region — the staff switcher — plus a setter and
 * the regions they're assigned to. Throws outside a `RegionProvider`.
 *
 * Named for the viewer because a record's owning member also has a `RegionId`,
 * and the two are indistinguishable at a callsite otherwise — picking the wrong
 * one is silent. This one governs presentation (units, currency, date format);
 * the member's governs which moment a record happened, and is passed explicitly
 * as a prop rather than read from ambient state.
 */
export function useViewerRegion(): RegionContextValue {
  const ctx = useContext(RegionContext)
  if (!ctx) throw new Error('useViewerRegion must be used within a RegionProvider')
  return ctx
}
